import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import confetti from 'canvas-confetti';
import PropTypes from 'prop-types';
import useAudio from '../../hooks/useAudio';
import useFirebase from '../../hooks/useFirebase';
import { Volume2, RotateCcw, Trophy, Star, Home, Shuffle } from 'lucide-react';
import { Link } from 'react-router-dom';
import useAnalytics from '../../hooks/useAnalytics';

// Scoring constants
const SCORING_CONSTANTS = {
  BASE_POINTS: 20,
  STREAK_MULTIPLIER: 2,
  SPEED_BONUS_MAX: 50,
  ACCURACY_BONUS_MAX: 100,
  PERFECT_ACCURACY_BONUS: 200,
  LEVEL_COMPLETE_BONUS: 150,
  MAX_SPEED_BONUS_TIME: 10, // seconds per match
  MIN_ACCURACY_FOR_STARS: 70
};

// Level configurations - now with rounds per level
const LEVEL_CONFIGS = {
  1: { 
    pairs: 4, 
    rounds: 10, // Number of rounds per level
    title: "Tiny Memory",
    description: "Start with 4 pairs, 10 rounds!",
    passingScore: 30
  },
  2: { 
    pairs: 6, 
    rounds: 10,
    title: "Small Steps",
    description: "6 pairs to match, 10 rounds!",
    passingScore: 30
  },
  3: { 
    pairs: 8, 
    rounds: 10,
    title: "Getting Better",
    description: "8 pairs challenge, 10 rounds!",
    passingScore: 30
  },
  4: { 
    pairs: 10, 
    rounds: 10,
    title: "Memory Master",
    description: "10 pairs test, 10 rounds!",
    passingScore: 30
  },
  5: { 
    pairs: 12, 
    rounds: 10,
    title: "Expert Level",
    description: "12 pairs mastery, 10 rounds!",
    passingScore: 30
  }
};

const ArabicMemoryCards = () => {
  // Initialize hooks
  const { saveSessionData } = useAnalytics();
  const { 
    userId,
    level,
    setLevel,
    maxLevel,
    levelsData,
    bestScores,
    isLoading,
    error,
    saveLevelData,
    updateLevel,
    unlockNextLevel,
    resetAllProgress
  } = useFirebase('WordMatchGameV2'); // Using existing game type for word pairs data

  const {
    playCorrectSound,
    playIncorrectSound,
    playPerfectSound,
    playStreakSound,
    playWhooshSound,
    playStarSound,
    startBackgroundMusic,
    AudioElements
  } = useAudio();

  // Refs for audio management
  const audioRef = useRef(null);
  const audioTimeoutRef = useRef(null);
  
  // Game state
  const [gamePhase, setGamePhase] = useState('menu'); // 'menu', 'playing', 'complete'
  const [currentRound, setCurrentRound] = useState(0);
  const [cards, setCards] = useState([]);
  const [flippedCards, setFlippedCards] = useState([]);
  const [matchedPairs, setMatchedPairs] = useState([]);
  const [selectedCards, setSelectedCards] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [availableWordPool, setAvailableWordPool] = useState([]);

  const [sessionStartTime, setSessionStartTime] = useState(Date.now());
  const [sessionMistakes, setSessionMistakes] = useState([]);
  
  // Scoring state
  const [totalScore, setTotalScore] = useState(0);
  const [correctMatches, setCorrectMatches] = useState(0);
  const [totalAttempts, setTotalAttempts] = useState(0);
  const [roundsCompleted, setRoundsCompleted] = useState(0);
  const [gameStartTime, setGameStartTime] = useState(Date.now());
  const [lastMatchTime, setLastMatchTime] = useState(Date.now());
  const [currentStreak, setCurrentStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [stars, setStars] = useState(0);
  
  // UI state
  const [celebrations, setCelebrations] = useState(false);
  const [sparkles, setSparkles] = useState(false);
  const [showLevelComplete, setShowLevelComplete] = useState(false);
  const [levelCompleteData, setLevelCompleteData] = useState(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  
  // Get current level config
  const levelConfig = useMemo(() => LEVEL_CONFIGS[level] || LEVEL_CONFIGS[1], [level]);
  
  // Utility functions
  const shuffleArray = useCallback((array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  }, []);

  // Create cumulative word pool for current level
  const createWordPool = useCallback(() => {
    if (!levelsData || Object.keys(levelsData).length === 0) {
      console.log('Levels data not loaded yet');
      return [];
    }

    const wordPool = [];
    
    // Add words from level 1 up to current level (cumulative)
    for (let i = 1; i <= level; i++) {
      const levelData = levelsData[i.toString()];
      if (levelData && levelData.wordPairs) {
        wordPool.push(...levelData.wordPairs);
      }
    }
    
    console.log(`Created word pool for level ${level} with ${wordPool.length} total words from levels 1-${level}`);
    return wordPool;
  }, [levelsData, level]);

  // Audio playback
  const playWordAudio = useCallback(async (audioUrl) => {
    if (!audioUrl) return;

    // Clear any existing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    
    if (audioTimeoutRef.current) {
      clearTimeout(audioTimeoutRef.current);
      audioTimeoutRef.current = null;
    }

    setAudioPlaying(true);

    try {
      const audio = new Audio();
      audioRef.current = audio;
      
      audioTimeoutRef.current = setTimeout(() => {
        cleanupAudio();
      }, 5000);
      
      const cleanupAudio = () => {
        setAudioPlaying(false);
        if (audioTimeoutRef.current) {
          clearTimeout(audioTimeoutRef.current);
          audioTimeoutRef.current = null;
        }
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('error', handleError);
        try {
          audio.pause();
          audio.currentTime = 0;
          audio.src = '';
        } catch (e) {
          // Ignore cleanup errors
        }
        audioRef.current = null;
      };
      
      const handleEnded = () => cleanupAudio();
      const handleError = () => cleanupAudio();
      
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('error', handleError);
      audio.volume = 0.8;
      audio.src = audioUrl;
      await audio.play();
      
    } catch (error) {
      console.error('Failed to play audio:', error);
      setAudioPlaying(false);
      if (audioTimeoutRef.current) {
        clearTimeout(audioTimeoutRef.current);
        audioTimeoutRef.current = null;
      }
    }
  }, []);

  const lastCelebrationRef = useRef(0);
  const triggerCelebration = useCallback((isStreak = false, earnedStars = 0) => {
    const now = Date.now();
    if (now - lastCelebrationRef.current < 500) {
      return;
    }
    lastCelebrationRef.current = now;
    
    setCelebrations(true);
    setSparkles(true);
    
    let colors = ['#FF6B6B', '#4ECDC4', '#45B7D1'];
    let particleCount = 30;
    let shapes = ['circle'];
    
    if (earnedStars > 0) {
      colors = ['#FFD700', '#FFA500'];
      shapes = ['star'];
      particleCount = 50;
      playStarSound();
    } else if (isStreak) {
      colors = ['#FFD700', '#FFA500'];
      particleCount = 40;
      playStreakSound();
    } else {
      playCorrectSound();
    }
    
    confetti({
      particleCount,
      spread: 50,
      origin: { y: 0.7 },
      colors,
      shapes,
      gravity: 1.2,
      drift: 0
    });

    setTimeout(() => {
      setCelebrations(false);
      setSparkles(false);
    }, 1500);
  }, [playStarSound, playStreakSound, playCorrectSound]);

  const calculateSpeedBonus = useCallback((responseTime) => {
    const maxTime = SCORING_CONSTANTS.MAX_SPEED_BONUS_TIME * 1000;
    if (responseTime <= maxTime) {
      const bonus = Math.round(SCORING_CONSTANTS.SPEED_BONUS_MAX * (1 - responseTime / maxTime));
      return Math.max(0, bonus);
    }
    return 0;
  }, []);

  const calculateStars = useCallback((accuracy, totalScore, totalPossibleMatches) => {
    const maxPossibleScore = totalPossibleMatches * (SCORING_CONSTANTS.BASE_POINTS + SCORING_CONSTANTS.SPEED_BONUS_MAX);
    const scorePercentage = (totalScore / maxPossibleScore) * 100;
    
    if (accuracy >= 95 && scorePercentage >= 85) return 3;
    if (accuracy >= 85 && scorePercentage >= 70) return 2;
    if (accuracy >= 70 && scorePercentage >= 50) return 1;
    return 0;
  }, []);

  // Initialize a new round
  const initializeRound = useCallback(() => {
    if (!availableWordPool || availableWordPool.length === 0) {
      console.log('Word pool not ready');
      return;
    }

    if (availableWordPool.length < levelConfig.pairs) {
      console.log(`Not enough words in pool. Need ${levelConfig.pairs}, have ${availableWordPool.length}`);
      return;
    }

    // Select random pairs for this round
    const shuffledPool = shuffleArray([...availableWordPool]);
    const selectedPairs = shuffledPool.slice(0, levelConfig.pairs);
    
    const gameCards = [];
    
    // Create pairs of cards (Arabic and English)
    selectedPairs.forEach((pair, index) => {
      // Arabic card
      gameCards.push({
        id: `arabic-${index}`,
        type: 'arabic',
        content: pair.arabic,
        word: pair.arabic,
        pronunciation: pair.pronunciation,
        emoji: pair.emoji,
        audio: pair.audio || pair.audioUrl,
        pairId: index,
        matchData: pair
      });
      
      // English card
      gameCards.push({
        id: `english-${index}`,
        type: 'english',
        content: pair.english,
        word: pair.english,
        emoji: pair.emoji,
        pairId: index,
        matchData: pair
      });
    });
    
    // Shuffle all cards
    const shuffledCards = shuffleArray(gameCards);
    setCards(shuffledCards);
    setFlippedCards([]);
    setMatchedPairs([]);
    setSelectedCards([]);
    setLastMatchTime(Date.now());
    setIsProcessing(false);
    
    console.log(`Initialized round ${currentRound + 1} with ${levelConfig.pairs} pairs`);
  }, [availableWordPool, levelConfig.pairs, currentRound, shuffleArray]);

  const handleCardClick = useCallback((card) => {
    if (isProcessing) return;
    if (flippedCards.includes(card.id)) return;
    if (matchedPairs.includes(card.id)) return;
    if (selectedCards.length >= 2) return;
    
    const newFlippedCards = [...flippedCards, card.id];
    const newSelectedCards = [...selectedCards, card];
    
    setFlippedCards(newFlippedCards);
    setSelectedCards(newSelectedCards);
    
    // Play audio for Arabic cards
    if (card.type === 'arabic' && card.audio) {
      playWordAudio(card.audio);
    }
    
    if (newSelectedCards.length === 2) {
      setIsProcessing(true);
      setTotalAttempts(prev => prev + 1);
      
      // Check for match
      const [firstCard, secondCard] = newSelectedCards;
      const isMatch = firstCard.pairId === secondCard.pairId && firstCard.type !== secondCard.type;
      
      if (isMatch) {
        // Match found!
        const responseTime = Date.now() - lastMatchTime;
        const speedBonus = calculateSpeedBonus(responseTime);
        const newStreak = currentStreak + 1;
        
        let points = SCORING_CONSTANTS.BASE_POINTS;
        if (newStreak >= 3) {
          points *= SCORING_CONSTANTS.STREAK_MULTIPLIER;
        }
        
        setTimeout(() => {
          setMatchedPairs(prev => [...prev, firstCard.id, secondCard.id]);
          setFlippedCards(prev => prev.filter(id => id !== firstCard.id && id !== secondCard.id));
          setSelectedCards([]);
          setCorrectMatches(prev => prev + 1);
          setCurrentStreak(newStreak);
          setBestStreak(prev => Math.max(prev, newStreak));
          setTotalScore(prev => prev + points + speedBonus);
          setIsProcessing(false);
          setLastMatchTime(Date.now());
          
          const isStreak = newStreak >= 3;
          triggerCelebration(isStreak);
          
          // Check if round complete
          const newMatchedCount = correctMatches + 1;
          if (newMatchedCount % levelConfig.pairs === 0) {
            // Round completed
            setTimeout(() => {
              handleRoundComplete();
            }, 1000);
          }
        }, 800);
      } else {
        // No match
        setSessionMistakes(prev => [...prev, {
          timestamp: Date.now(),
          type: 'wrong_memory_match',
          attempted: {
            card1: selectedCards[0]?.content,
            card2: card?.content
          },
          round: currentRound,
          attempt: totalAttempts + 1
        }]);
        playIncorrectSound();
        setCurrentStreak(0);
        setTimeout(() => {
          setFlippedCards(prev => prev.filter(id => id !== firstCard.id && id !== secondCard.id));
          setSelectedCards([]);
          setIsProcessing(false);
        }, 1500);
      }
    }
  }, [
    isProcessing,
    flippedCards,
    matchedPairs,
    selectedCards,
    currentStreak,
    correctMatches,
    levelConfig.pairs,
    calculateSpeedBonus,
    playWordAudio,
    playIncorrectSound,
    triggerCelebration
  ]);

  const handleRoundComplete = useCallback(() => {
    const newRoundsCompleted = roundsCompleted + 1;
    setRoundsCompleted(newRoundsCompleted);
    
    playWhooshSound();
    
    if (newRoundsCompleted >= levelConfig.rounds) {
      // Level complete
      setTimeout(() => {
        handleLevelComplete();
      }, 1000);
    } else {
      // Move to next round
      setCurrentRound(prev => prev + 1);
      setTimeout(() => {
        initializeRound();
      }, 2000);
    }
  }, [roundsCompleted, levelConfig.rounds, playWhooshSound, initializeRound]);

  const handleLevelComplete = useCallback(() => {
    const totalPossibleMatches = levelConfig.rounds * levelConfig.pairs;
    const accuracy = Math.round((correctMatches / totalPossibleMatches) * 100);
    const finalScore = totalScore + SCORING_CONSTANTS.LEVEL_COMPLETE_BONUS;
    const earnedStars = calculateStars(accuracy, finalScore, totalPossibleMatches);
    
    const scoreData = {
      totalScore: finalScore,
      accuracy,
      stars: earnedStars,
      attempts: totalAttempts,
      correctMatches,
      totalPossibleMatches,
      bestStreak,
      roundsCompleted: levelConfig.rounds,
      completedAt: new Date().toISOString()
    };
    
    setStars(earnedStars);
    saveLevelData(level, scoreData);

    const sessionData = {
      gameType: 'arabicMemoryCards',
      level,
      sessionStartTime,
      sessionDuration: Math.round((Date.now() - sessionStartTime) / 1000),
      totalScore: finalScore,
      accuracy,
      attempts: totalAttempts,
      correctAnswers: correctMatches,
      mistakes: sessionMistakes,
      starsEarned: earnedStars,
      bestStreak,
      roundsCompleted: levelConfig.rounds
    };
    saveSessionData(sessionData);
    
    const nextLevel = level + 1;
    const isLastLevel = !LEVEL_CONFIGS[nextLevel];
    const passedLevel = accuracy >= levelConfig.passingScore;
    
    setLevelCompleteData({
      ...scoreData,
      currentLevel: level,
      nextLevel,
      isLastLevel,
      passedLevel,
      levelTitle: levelConfig.title,
      passingScore: levelConfig.passingScore
    });
    
    if (earnedStars > 0) {
      triggerCelebration(false, earnedStars);
    }
    
    playPerfectSound();
    setGamePhase('complete');
    setShowLevelComplete(true);
    
    if (passedLevel && !isLastLevel) {
      unlockNextLevel();
    }
  }, [
    levelConfig.rounds,
    levelConfig.pairs,
    levelConfig.passingScore,
    levelConfig.title,
    correctMatches,
    totalAttempts,
    totalScore,
    bestStreak,
    level,
    calculateStars,
    saveLevelData,
    triggerCelebration,
    playPerfectSound,
    unlockNextLevel
  ]);

  const startLevel = useCallback(() => {
    setSessionStartTime(Date.now()); 
    setSessionMistakes([]);
    setGamePhase('playing');
    setCurrentRound(0);
    setCorrectMatches(0);
    setTotalAttempts(0);
    setRoundsCompleted(0);
    setTotalScore(0);
    setCurrentStreak(0);
    setBestStreak(0);
    setGameStartTime(Date.now());
    
    setTimeout(() => {
      initializeRound();
    }, 100);
  }, [initializeRound]);

  const goToMenu = useCallback(() => {
    setGamePhase('menu');
  }, []);

  const restartLevel = useCallback(() => {
    startLevel();
  }, [startLevel]);

  const handleNextLevel = useCallback(() => {
    if (levelCompleteData?.passedLevel && !levelCompleteData?.isLastLevel) {
      updateLevel(levelCompleteData.nextLevel);
    }
    setShowLevelComplete(false);
    setLevelCompleteData(null);
    setGamePhase('menu');
  }, [levelCompleteData, updateLevel]);

  const handleResetProgress = useCallback(() => {
    setShowResetConfirmation(true);
  }, []);

  const confirmResetProgress = useCallback(() => {
    resetAllProgress();
    updateLevel(1);
    setGamePhase('menu');
    setShowResetConfirmation(false);
  }, [resetAllProgress, updateLevel]);

  const selectLevel = useCallback((newLevel) => {
    setLevel(newLevel);
  }, [setLevel]);

  // Update word pool when level or data changes
  useEffect(() => {
    const wordPool = createWordPool();
    setAvailableWordPool(wordPool);
  }, [createWordPool]);

  // Initialize round when word pool is ready
  useEffect(() => {
    if (gamePhase === 'playing' && availableWordPool.length > 0) {
      initializeRound();
    }
  }, [gamePhase, availableWordPool, initializeRound]);

  // Start background music
  useEffect(() => {
    const musicTimer = setTimeout(() => {
      startBackgroundMusic();
    }, 1000);
    return () => clearTimeout(musicTimer);
  }, [startBackgroundMusic]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        try {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          audioRef.current.src = '';
        } catch (e) {
          // Ignore
        }
        audioRef.current = null;
      }
      if (audioTimeoutRef.current) {
        clearTimeout(audioTimeoutRef.current);
        audioTimeoutRef.current = null;
      }
      confetti.reset();
    };
  }, []);

  // Components
  const StarDisplay = React.memo(({ count, size = "text-2xl" }) => (
    <div className={`flex justify-center gap-1 ${size}`}>
      {[1, 2, 3].map(star => (
        <span 
          key={star} 
          className={`drop-shadow-lg transition-all duration-300 ${star <= count ? 'text-yellow-400 animate-pulse' : 'text-gray-300'}`}
        >
          ⭐
        </span>
      ))}
    </div>
  ));

  StarDisplay.propTypes = {
    count: PropTypes.number.isRequired,
    size: PropTypes.string
  };

  const MemoryCard = React.memo(({ card }) => {
    const isFlipped = flippedCards.includes(card.id) || matchedPairs.includes(card.id);
    const isMatched = matchedPairs.includes(card.id);
    const isSelected = selectedCards.some(selected => selected.id === card.id);
    
    return (
      <div
        onClick={() => handleCardClick(card)}
        className={`
          relative w-full aspect-square rounded-2xl border-4 cursor-pointer transform transition-all duration-500 hover:scale-105
          ${isFlipped 
            ? isMatched 
              ? 'bg-gradient-to-br from-green-400 to-green-500 border-green-600 shadow-2xl' 
              : isSelected
                ? 'bg-gradient-to-br from-blue-400 to-blue-500 border-blue-600 shadow-xl'
                : 'bg-gradient-to-br from-purple-400 to-purple-500 border-purple-600 shadow-xl'
            : 'bg-gradient-to-br from-gray-300 to-gray-400 border-gray-500 shadow-lg hover:shadow-xl'
          }
        `}
        style={{
          transformStyle: 'preserve-3d',
          transform: isFlipped ? 'rotateY(0deg)' : 'rotateY(180deg)'
        }}
      >
        {/* Card Back */}
        <div 
          className="absolute inset-0 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 border-4 border-white flex items-center justify-center"
          style={{
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)'
          }}
        >
          <div className="text-4xl md:text-6xl">🧠</div>
        </div>
        
        {/* Card Front */}
        <div 
          className="absolute inset-0 rounded-2xl flex flex-col items-center justify-center p-2"
          style={{
            backfaceVisibility: 'hidden'
          }}
        >
          <div className="text-center">
            {card.emoji && (
              <div className="text-2xl md:text-3xl mb-2">{card.emoji}</div>
            )}
            <div className={`font-black text-white drop-shadow-2xl ${
              card.type === 'arabic' ? 'text-2xl md:text-3xl' : 'text-lg md:text-xl'
            }`}>
              {card.content}
            </div>
            {card.type === 'arabic' && card.pronunciation && (
              <div className="text-white text-xs md:text-sm mt-1 opacity-80">
                {card.pronunciation}
              </div>
            )}
          </div>
          
          {isMatched && card.type === 'arabic' && card.audio && (
            <div className="absolute -top-2 -right-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  playWordAudio(card.audio);
                }}
                disabled={audioPlaying}
                className="bg-yellow-400 text-yellow-800 p-2 rounded-full shadow-lg hover:bg-yellow-300 transition-all duration-200"
              >
                <Volume2 size={16} />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  });

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-400 via-purple-500 to-indigo-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-sm w-full">
          <div className="text-center">
            <div className="text-4xl md:text-6xl mb-4 animate-spin">🧠</div>
            <div className="text-purple-600 text-xl md:text-3xl font-bold animate-pulse">Loading Memory Mirage...</div>
            <div className="text-gray-600 text-sm md:text-lg mt-2">Getting ready for brain training!</div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-400 via-purple-500 to-indigo-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-2xl">
          <div className="text-4xl md:text-6xl mb-4">😢</div>
          <h2 className="text-xl md:text-3xl font-bold text-red-600 mb-4">Oops! Something went wrong</h2>
          <p className="text-gray-700 mb-6 text-sm md:text-lg">{error?.message || 'Something went wrong'}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-2xl font-bold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
          >
            🔄 Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-400 via-purple-500 to-indigo-600 p-4">
      <AudioElements />
      
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-4 md:mb-8">
          <h1 className="text-3xl md:text-6xl font-black text-white mb-2 md:mb-4 drop-shadow-2xl">
            🧠 Memory Mirage 🧠
          </h1>
          <p className="text-lg md:text-2xl text-white font-bold opacity-90 drop-shadow-lg">
            Match Arabic words with their English translations!
          </p>
        </div>

        {gamePhase === 'menu' && (
          <div className="max-w-4xl mx-auto">
            {/* Level Info */}
            <div className="bg-white rounded-3xl shadow-2xl p-4 md:p-8 mb-4 md:mb-8 border-4 border-yellow-300">
              <h2 className="text-2xl md:text-4xl font-bold text-center mb-2 md:mb-4 text-purple-600">
                🎮 Level {level}: {levelConfig.title}
              </h2>
              <p className="text-center text-gray-700 mb-4 md:mb-6 text-lg md:text-xl font-semibold">
                {levelConfig.description}
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-4 md:mb-8">
                <div className="text-center bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl p-3 md:p-6 text-white">
                  <div className="text-3xl md:text-5xl mb-1 md:mb-3">🎯</div>
                  <p className="font-bold text-sm md:text-lg">Pairs per Round</p>
                  <p className="text-lg md:text-2xl font-black">{levelConfig.pairs}</p>
                </div>
                <div className="text-center bg-gradient-to-br from-green-400 to-green-600 rounded-2xl p-3 md:p-6 text-white">
                  <div className="text-3xl md:text-5xl mb-1 md:mb-3">🔄</div>
                  <p className="font-bold text-sm md:text-lg">Rounds</p>
                  <p className="text-lg md:text-2xl font-black">{levelConfig.rounds}</p>
                </div>
                <div className="text-center bg-gradient-to-br from-orange-400 to-orange-600 rounded-2xl p-3 md:p-6 text-white">
                  <div className="text-3xl md:text-5xl mb-1 md:mb-3">📚</div>
                  <p className="font-bold text-sm md:text-lg">Word Pool</p>
                  <p className="text-lg md:text-2xl font-black">Levels 1-{level}</p>
                </div>
                <div className="text-center bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl p-3 md:p-6 text-white">
                  <div className="text-3xl md:text-5xl mb-1 md:mb-3">🏆</div>
                  <p className="font-bold text-sm md:text-lg">Pass</p>
                  <p className="text-lg md:text-2xl font-black">{levelConfig.passingScore}%</p>
                </div>
              </div>

              <div className="text-center">
                <button
                  onClick={startLevel}
                  className="px-8 md:px-12 py-4 md:py-6 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-3xl font-black text-xl md:text-3xl hover:shadow-2xl transform hover:scale-110 transition-all duration-300 border-4 border-white"
                >
                  🧠 START MEMORY GAME! 🧠
                </button>
              </div>
            </div>

            {/* Level Grid */}
            <div className="bg-white rounded-3xl shadow-2xl p-4 md:p-8 border-4 border-pink-300">
              <h3 className="text-xl md:text-3xl font-bold text-center mb-4 md:mb-6 text-purple-600">
                🎮 Choose Your Level!
              </h3>
              <div className="grid grid-cols-3 md:grid-cols-5 gap-2 md:gap-4">
                {Object.entries(LEVEL_CONFIGS).map(([levelNum, config]) => {
                  const num = parseInt(levelNum);
                  const isUnlocked = num <= maxLevel;
                  const isCurrent = num === level;
                  const levelScore = bestScores[`level${num}`];
                  
                  return (
                    <button
                      key={num}
                      onClick={() => isUnlocked ? selectLevel(num) : null}
                      disabled={!isUnlocked}
                      className={`
                        relative p-2 md:p-4 rounded-2xl text-sm md:text-lg font-bold transition-all duration-300 min-h-[80px] md:min-h-[120px] flex flex-col items-center justify-center border-2 md:border-4
                        ${isCurrent 
                          ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-2xl transform scale-105 md:scale-110 border-yellow-300' 
                          : isUnlocked 
                            ? 'bg-gradient-to-br from-green-100 to-green-200 text-green-800 hover:from-green-200 hover:to-green-300 cursor-pointer hover:scale-105 border-green-300' 
                            : 'bg-gray-200 text-gray-400 cursor-not-allowed border-gray-300'
                        }
                      `}
                      title={isUnlocked ? config.title : 'Locked'}
                    >
                      <div className="text-lg md:text-2xl font-black">{num}</div>
                      <div className="text-xs mt-1 font-bold hidden md:block">{config.pairs} pairs</div>
                      <div className="text-xs font-bold md:hidden">{config.pairs}p</div>
                      <div className="text-xs font-bold">{config.rounds} rounds</div>
                      {levelScore && (
                        <div className="absolute -top-1 -right-1 md:-top-2 md:-right-2">
                          <StarDisplay count={levelScore.stars || 0} size="text-xs md:text-sm" />
                        </div>
                      )}
                      {!isUnlocked && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-xl md:text-3xl">🔒</span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              
              <div className="mt-4 md:mt-8 flex gap-2 md:gap-4 justify-center flex-wrap">
                <Link to="/">
                <button 
                  className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  🔙 Main Menu
                </button>
                </Link>
                <button 
                  onClick={handleResetProgress}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  🗑️ Reset Progress
                </button>
                <button 
                  onClick={() => setShowInstructions(true)}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  📖 How to Play
                </button>
              </div>
            </div>
          </div>
        )}

        {gamePhase === 'playing' && (
          <div className="max-w-4xl mx-auto">
            {/* Progress and Stats */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 md:p-6 mb-6 shadow-xl">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl md:text-2xl font-bold text-purple-600">
                  Round {currentRound + 1} of {levelConfig.rounds}
                </h3>
                <div className="text-right">
                  <p className="text-lg font-bold text-indigo-600">{totalScore} points</p>
                  <p className="text-sm text-gray-600">{roundsCompleted} rounds done</p>
                </div>
              </div>
              
              {/* Progress bar */}
              <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
                <div 
                  className="bg-gradient-to-r from-green-400 to-green-500 h-4 rounded-full transition-all duration-500"
                  style={{ width: `${(roundsCompleted / levelConfig.rounds) * 100}%` }}
                ></div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div className="bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl p-3 text-white">
                  <div className="text-2xl md:text-3xl mb-1">🎯</div>
                  <p className="font-bold text-lg md:text-xl">{totalScore}</p>
                  <p className="text-xs font-bold">Score</p>
                </div>
                <div className="bg-gradient-to-br from-green-400 to-green-600 rounded-xl p-3 text-white">
                  <div className="text-2xl md:text-3xl mb-1">✅</div>
                  <p className="font-bold text-lg md:text-xl">{correctMatches}/{levelConfig.rounds * levelConfig.pairs}</p>
                  <p className="text-xs font-bold">Matches</p>
                </div>
                <div className="bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl p-3 text-white">
                  <div className="text-2xl md:text-3xl mb-1">🔄</div>
                  <p className="font-bold text-lg md:text-xl">{totalAttempts}</p>
                  <p className="text-xs font-bold">Attempts</p>
                </div>
                <div className="bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl p-3 text-white">
                  <div className="text-2xl md:text-3xl mb-1">🔥</div>
                  <p className="font-bold text-lg md:text-xl">{currentStreak}</p>
                  <p className="text-xs font-bold">Streak</p>
                </div>
              </div>
              
              {currentStreak >= 3 && (
                <div className="mt-4 text-center bg-gradient-to-r from-orange-400/80 to-red-400/80 rounded-xl p-3">
                  <p className="text-white font-bold text-lg animate-pulse">
                    🔥 {currentStreak} in a row! You're on fire! 🔥
                  </p>
                </div>
              )}
            </div>

            {/* Game Board */}
            <div className="bg-white rounded-3xl shadow-2xl p-4 md:p-8 mb-6 border-4 border-yellow-300">
              <div className="text-center mb-4">
                <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-2">
                  🧠 Round {currentRound + 1}: Match {levelConfig.pairs} pairs!
                </h2>
                <p className="text-gray-600">Words from levels 1-{level} • {matchedPairs.length / 2}/{levelConfig.pairs} pairs matched</p>
              </div>
              
              <div className={`grid gap-3 md:gap-4 ${
                levelConfig.pairs <= 4 ? 'grid-cols-4' : 
                levelConfig.pairs <= 6 ? 'grid-cols-4 md:grid-cols-6' : 
                'grid-cols-4 md:grid-cols-6 lg:grid-cols-8'
              }`}>
                {cards.map((card) => (
                  <MemoryCard key={card.id} card={card} />
                ))}
              </div>
            </div>

            {/* Controls */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 md:p-6 shadow-xl">
              <div className="flex gap-3 md:gap-4 justify-center flex-wrap">
                <button 
                  onClick={goToMenu}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                >
                  <Home size={20} />
                  Menu
                </button>
                <button 
                  onClick={restartLevel}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                >
                  <RotateCcw size={20} />
                  Restart Level
                </button>
                <button 
                  onClick={() => initializeRound()}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                >
                  <Shuffle size={20} />
                  New Round
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Celebration Effects */}
        {celebrations && (
          <div className="fixed inset-0 pointer-events-none z-40">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-6xl md:text-9xl animate-bounce">🎉</div>
            </div>
          </div>
        )}

        {sparkles && (
          <div className="fixed inset-0 pointer-events-none z-40">
            {[...Array(15)].map((_, i) => (
              <div
                key={`sparkle-${i}`}
                className="absolute animate-ping text-yellow-400 text-2xl"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`
                }}
              >
                ✨
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Instructions Modal */}
      {showInstructions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border-4 border-purple-300">
            <div className="p-4 md:p-8">
              <div className="flex justify-between items-center mb-4 md:mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-purple-600">📖 How to Play Memory Cards!</h2>
                <button 
                  onClick={() => setShowInstructions(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl md:text-3xl font-bold w-10 h-10 flex items-center justify-center"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4 md:space-y-6">
                <div className="bg-gradient-to-r from-blue-100 to-blue-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-blue-300">
                  <h3 className="font-bold text-blue-800 mb-2 md:mb-3 text-lg md:text-xl">🎯 Your Mission</h3>
                  <p className="text-blue-700 text-sm md:text-lg">Complete multiple rounds of memory games! Each round, match Arabic words with their English translations. As levels progress, you'll learn more words!</p>
                </div>
                
                <div className="bg-gradient-to-r from-green-100 to-green-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-green-300">
                  <h3 className="font-bold text-green-800 mb-2 md:mb-3 text-lg md:text-xl">🎮 How to Play</h3>
                  <ul className="list-disc list-inside text-green-700 space-y-1 md:space-y-2 text-sm md:text-lg">
                    <li>Each level has {levelConfig.rounds} rounds with {levelConfig.pairs} pairs each</li>
                    <li>Click on any card to flip it and see what's underneath</li>
                    <li>Click on a second card to see if they match</li>
                    <li>If they match (Arabic word + English translation), they stay flipped!</li>
                    <li>If they don't match, they flip back over</li>
                    <li>Complete all pairs to move to the next round</li>
                    <li>Complete all rounds to finish the level!</li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-r from-yellow-100 to-yellow-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-yellow-300">
                  <h3 className="font-bold text-yellow-800 mb-2 md:mb-3 text-lg md:text-xl">📚 Learning System</h3>
                  <ul className="list-disc list-inside text-yellow-700 space-y-1 md:space-y-2 text-sm md:text-lg">
                    <li>Level 1: Uses only Level 1 words from your lessons</li>
                    <li>Level 2: Uses Level 1 + Level 2 words (more variety!)</li>
                    <li>Level 3: Uses Level 1 + Level 2 + Level 3 words</li>
                    <li>Each level builds on previous knowledge</li>
                    <li>Higher levels = more word choices = more challenge!</li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-r from-purple-100 to-purple-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-purple-300">
                  <h3 className="font-bold text-purple-800 mb-2 md:mb-3 text-lg md:text-xl">🔊 Audio Help</h3>
                  <ul className="list-disc list-inside text-purple-700 space-y-1 md:space-y-2 text-sm md:text-lg">
                    <li>When you flip an Arabic card, you'll hear how to pronounce it!</li>
                    <li>After matching, click the speaker button to hear it again</li>
                    <li>This helps you learn proper Arabic pronunciation</li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-r from-red-100 to-red-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-red-300">
                  <h3 className="font-bold text-red-800 mb-2 md:mb-3 text-lg md:text-xl">💡 Pro Tips</h3>
                  <ul className="list-disc list-inside text-red-700 space-y-1 md:space-y-2 text-sm md:text-lg">
                    <li>Start by flipping cards systematically to learn positions</li>
                    <li>Focus on accuracy to build up your streak</li>
                    <li>Each round uses different word combinations</li>
                    <li>Higher levels introduce words from earlier lessons too</li>
                    <li>Get {levelConfig.passingScore}% accuracy to unlock the next level!</li>
                  </ul>
                </div>
              </div>
              
              <div className="mt-4 md:mt-8 text-center">
                <button 
                  onClick={() => setShowInstructions(false)}
                  className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Let's Train My Memory! 🧠
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Level Complete Modal */}
      {showLevelComplete && levelCompleteData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-4 md:p-8 border-4 border-yellow-300">
            <div className="text-center">
              <div className="text-6xl md:text-8xl mb-4 md:mb-6">
                {levelCompleteData.passedLevel ? '🎉' : '😔'}
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-purple-600 mb-2 md:mb-3">
                Level {levelCompleteData.currentLevel} Complete!
              </h2>
              <h3 className="text-lg md:text-xl text-gray-700 mb-4 md:mb-6 font-semibold">
                {levelCompleteData.levelTitle}
              </h3>
              
              <div className="mb-6 md:mb-8">
                <StarDisplay count={levelCompleteData.stars} size="text-3xl md:text-4xl" />
                <p className="text-xl md:text-2xl font-bold text-indigo-600 mt-3 md:mt-4">
                  Final Score: {levelCompleteData.totalScore}
                </p>
                <p className="text-base md:text-lg text-gray-600 mt-2">
                  Accuracy: {levelCompleteData.accuracy}% • Matches: {levelCompleteData.correctMatches}/{levelCompleteData.totalPossibleMatches}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Completed {levelCompleteData.roundsCompleted} rounds • {levelCompleteData.attempts} total attempts
                </p>
              </div>

              <div className="mb-6 md:mb-8">
                {levelCompleteData.passedLevel ? (
                  <div className="bg-gradient-to-r from-green-100 to-green-200 border-2 md:border-4 border-green-400 rounded-2xl p-4 md:p-6">
                    <p className="text-green-800 font-bold text-lg md:text-xl">
                      ✅ Fantastic! You passed! 🎉
                    </p>
                    <p className="text-green-700 text-base md:text-lg">
                      {levelCompleteData.accuracy}% ≥ {levelCompleteData.passingScore}%
                    </p>
                    {!levelCompleteData.isLastLevel && (
                      <p className="text-green-700 text-base md:text-lg mt-2">Your memory is getting stronger! Ready for more words?</p>
                    )}
                  </div>
                ) : (
                  <div className="bg-gradient-to-r from-orange-100 to-orange-200 border-2 md:border-4 border-orange-400 rounded-2xl p-4 md:p-6">
                    <p className="text-orange-800 font-bold text-lg md:text-xl">
                      😔 Almost there! Keep training your memory!
                    </p>
                    <p className="text-orange-700 text-base md:text-lg">
                      You need {levelCompleteData.passingScore}% accuracy to pass
                    </p>
                    <p className="text-orange-700 text-base md:text-lg">You got {levelCompleteData.accuracy}% - so close! Try again!</p>
                  </div>
                )}
              </div>

              <div className="space-y-3 md:space-y-4">
                <button 
                  onClick={() => { 
                    setShowLevelComplete(false); 
                    setGamePhase('menu'); 
                  }}
                  className="w-full px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  🔄 Play Again
                </button>
                
                {levelCompleteData.passedLevel && !levelCompleteData.isLastLevel && (
                  <button 
                    onClick={handleNextLevel}
                    className="w-full px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-2xl transform hover:scale-110 transition-all duration-300"
                  >
                    🚀 Next Level! 🚀
                  </button>
                )}
                
                {levelCompleteData.isLastLevel && (
                  <button 
                    onClick={() => { 
                      setShowLevelComplete(false); 
                      setGamePhase('menu'); 
                    }}
                    className="w-full px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-2xl transform hover:scale-110 transition-all duration-300"
                  >
                    🎊 You're a Memory Master! 🎊
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Confirmation Modal */}
      {showResetConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-4 md:p-8 border-4 border-red-300">
            <div className="text-center">
              <div className="text-6xl md:text-8xl mb-4 md:mb-6">⚠️</div>
              <h2 className="text-2xl md:text-3xl font-bold text-red-600 mb-3 md:mb-4">Reset Everything?</h2>
              <p className="text-gray-700 mb-6 md:mb-8 text-sm md:text-lg">
                This will delete all your amazing scores, stars, and progress. You'll start from Level 1 again.
                This cannot be undone!
              </p>
              <div className="flex gap-3 md:gap-4 justify-center">
                <button 
                  onClick={() => setShowResetConfirmation(false)}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmResetProgress}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Yes, Reset Everything
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArabicMemoryCards;